import React, { useRef } from 'react';
import { ImportDownload } from '~/Components/Icons/ImportDownload';
import { ItemCreationToolTip, ItemCreationToolTipProps } from '~/Components';
import { useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '~/UIKit/Style';
import { useTranslation } from 'react-i18next';
import { ToastVariant, useToastMessage } from '~/Hooks';
interface IProps {
  handleFile: (file: File | null) => void;
  tooltip?: string;
  data?: File;
  typeCheck?: string;
}

const useStyles = makeStyles(() => ({
  uploadRectangleStyles: {
    boxSizing: 'border-box',
    minHeight: '100px',
    padding: '20px 0',
    width: '100%',
    border: '1px dashed #0084F8',
    borderRadius: '1px',
    backgroundColor: 'rgba(0,132,248,0.07)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '30px 0',
    fontSize: '12px',
  },
  chooseFile: {
    color: '#0084F8',
    cursor: 'pointer',
  },
  uploadIcon: {
    width: '23px',
  },
  fileSelector: {
    display: 'none',
  },
  dragUploadFile: {
    display: 'grid',
    gridAutoFlow: 'column',
    columnGap: '4px',
    alignItems: 'center',
  },
}));
export const DropArea = ({ handleFile, data, tooltip, typeCheck }: IProps) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const imgInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { showToast } = useToastMessage();

  const tooltipObj = tooltip
    ? ({
        title: tooltip,
        placement: isMobile ? 'bottom' : 'auto-start',
      } as ItemCreationToolTipProps)
    : undefined;

  const handleImageUpload = () => {
    if (imgInputRef?.current) {
      imgInputRef?.current?.click?.();
    }
  };

  const setUploadFile = (files = [] as File[]) => {
    if (files?.length === 0) {
      return false;
    }

    const file = files?.[0];
    const { type } = file;
    handleFile(null);
    if (typeCheck && !type.includes(typeCheck)) {
      showToast(file.name, t('app.dragarea.error.filetype', { typeCheck }), ToastVariant.ERROR);
      return false;
    }
    handleFile(files[0]);
  };

  const onClickUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setUploadFile(Array.from(e?.target?.files));
    }
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const {
      dataTransfer: { files },
    } = e as any;
    setUploadFile(files);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <div onDrop={(e) => onDrop(e)} onDragOver={(e) => onDragOver(e)} ref={containerRef}>
      {!data ? (
        <div className={classes.uploadRectangleStyles}>
          <div className={classes.uploadIcon}>
            <ImportDownload />
          </div>
          <span className={classes.dragUploadFile}>
            {isMobile ? '' : t('app.dragarea.text.before.choosefile')}
            <span className={classes.chooseFile} onClick={handleImageUpload}>
              {t('app.dragarea.text.choosefile')}
            </span>
            {t('app.dragarea.text.after.choosefile')}
            {tooltipObj && <ItemCreationToolTip {...tooltipObj} containerRef={containerRef} />}
          </span>
          <input
            className={classes.fileSelector}
            type="file"
            ref={imgInputRef}
            onChange={(e) => onClickUpload(e)}
          />
        </div>
      ) : null}
    </div>
  );
};
